// src/components/rankings/FilterPillSelects.jsx
//
// Pill-style filter triggers, ported from
// apps/web-app/src/components/shared/FilterPill.jsx +
// apps/web-app/src/components/shared/FilterPillSelects.jsx (only the parts the
// rankings page needs: the FilterPill trigger and the checkbox-style
// MultiSelectPill used for the Teams / Positions filters).
import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { X, Plus } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from 'components/ui/popover';
import { Checkbox } from 'components/ui/checkbox';
import { Label } from 'components/ui/label';
import { cn } from 'lib/utils';

/**
 * Pill-shaped trigger for a single filter. Visual state:
 *
 *   inactive → "[ + Teams ]"          (subtle border, no value, plus icon)
 *   active   → "[ Teams · 2  ✕ ]"    (filled, shows value, clear button)
 *
 * `onClear` is optional. When provided AND `active` is true, an inline ✕
 * appears that fires `onClear()` without opening the wrapping popover. The
 * clear control is a span+role=button to avoid nested <button> elements.
 */
export const FilterPill = forwardRef(function FilterPill(
  { active = false, label, value, onClear, className, children, ...buttonProps },
  ref
) {
  const handleClearKey = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      onClear?.();
    }
  };

  return (
    <button
      ref={ref}
      type="button"
      data-active={active ? 'true' : 'false'}
      data-compact
      style={{ fontSize: 14, lineHeight: 1 }}
      className={cn(
        'inline-flex shrink-0 items-center gap-1 rounded-full border py-2 px-2.5 font-medium whitespace-nowrap transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
        active
          ? 'bg-brand-purple1 text-white border-brand-purple1 hover:bg-brand-purple2'
          : 'bg-transparent text-brand-purple1 border-brand-purple1 hover:bg-brand-purple1/5',
        className
      )}
      {...buttonProps}
    >
      {!active && (
        <Plus className="w-3 h-3 -ml-0.5 opacity-60" aria-hidden="true" />
      )}
      <span>{label}</span>
      {active && value && (
        <>
          <span className="opacity-50" aria-hidden="true">·</span>
          <span className="truncate max-w-[140px] font-normal">{value}</span>
        </>
      )}
      {active && onClear && (
        <span
          role="button"
          tabIndex={0}
          data-compact
          aria-label={`Clear ${label} filter`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClear();
          }}
          onKeyDown={handleClearKey}
          className="inline-flex items-center justify-center w-3.5 h-3.5 rounded-full hover:bg-white/20 cursor-pointer"
        >
          <X className="w-3 h-3" aria-hidden="true" />
        </span>
      )}
      {children}
    </button>
  );
});

FilterPill.propTypes = {
  active: PropTypes.bool,
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  onClear: PropTypes.func,
  className: PropTypes.string,
  children: PropTypes.node,
};

/** Checkbox-style popover for multi-select filters (Teams, Positions). */
export function MultiSelectPill({ label, options, selected, onChange }) {
  const [open, setOpen] = React.useState(false);
  const active = selected.length > 0;
  const value = active ? `${selected.length}` : null;

  const toggle = (val) => {
    onChange(
      selected.includes(val)
        ? selected.filter((v) => v !== val)
        : [...selected, val]
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <FilterPill
          label={label}
          value={value || undefined}
          active={active}
          onClear={active ? () => onChange([]) : undefined}
          aria-haspopup="listbox"
          aria-expanded={open}
        />
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={6}
        className="w-60 max-h-[300px] overflow-y-auto p-1"
      >
        {options.map((opt) => {
          const id = `pill-${label}-${opt.value}`;
          const isChecked = selected.includes(opt.value);
          return (
            <div
              key={opt.value}
              className="flex items-center gap-2 rounded-token-md px-2 py-2 hover:bg-neutral-100 cursor-pointer"
              onClick={() => toggle(opt.value)}
            >
              <Checkbox
                id={id}
                checked={isChecked}
                onCheckedChange={() => toggle(opt.value)}
                onClick={(e) => e.stopPropagation()}
              />
              {opt.logoUrl && (
                <img src={opt.logoUrl} alt="" className="w-5 h-5 shrink-0" />
              )}
              <Label htmlFor={id} className="cursor-pointer font-normal flex-1">
                {opt.label}
              </Label>
            </div>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}

MultiSelectPill.propTypes = {
  label: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
      logoUrl: PropTypes.string,
    })
  ).isRequired,
  selected: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
};
